import { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import type { AccessPolicy, AccessPolicyUpdate } from "@/api/admin-api"
import type { DirectorySearchResult } from "@/services/directory-search-service"

import { DirectorySearch } from "@/components/directory-search"
import { AlertTriangle, Trash2, User, Users } from "@/components/icons"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Spinner } from "@/components/ui/spinner"
import { Switch } from "@/components/ui/switch"
import { cn } from "@/lib/utils"
import { DirectorySearchService } from "@/services/directory-search-service"

type AccessEntryType = "user" | "group"

type AccessEntry = {
  oid: string
  type: AccessEntryType
  displayName?: string
}

type ResolvedDirectoryMap = Partial<Record<string, DirectorySearchResult>>

function normalizePolicy(policy?: AccessPolicy): AccessPolicyUpdate {
  return {
    unrestricted: policy?.unrestricted === true,
    requires: {
      oids: policy?.requires?.oids ?? [],
      groups: policy?.requires?.groups ?? [],
    },
    block: {
      oids: policy?.block?.oids ?? [],
      groups: policy?.block?.groups ?? [],
    },
  }
}

function toEntries(list: { oids: string[]; groups: string[] }): AccessEntry[] {
  return [
    ...list.oids.map((oid) => ({ oid, type: "user" as const })),
    ...list.groups.map((oid) => ({ oid, type: "group" as const })),
  ]
}

function toUpdatePayload(
  unrestricted: boolean,
  requires: AccessEntry[],
  block: AccessEntry[]
): AccessPolicyUpdate {
  return {
    unrestricted,
    requires: {
      oids: requires.filter((e) => e.type === "user").map((e) => e.oid),
      groups: requires.filter((e) => e.type === "group").map((e) => e.oid),
    },
    block: {
      oids: block.filter((e) => e.type === "user").map((e) => e.oid),
      groups: block.filter((e) => e.type === "group").map((e) => e.oid),
    },
  }
}

export type AccessPolicyDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  policy?: AccessPolicy
  isLoading?: boolean
  /**
   * The stored policy (or the inherited baseline) could not be loaded. The
   * dialog then refuses to edit or save: an empty list here would mean
   * "nobody", and saving it would overwrite the real policy.
   */
  loadError?: boolean
  isSaving?: boolean
  inherit?: {
    enabled: boolean
    inheritByDefault?: boolean
    label?: string
    description?: string
    policy?: AccessPolicy
    onSaveInherited?: () => Promise<void>
  }
  onSave: (policy: AccessPolicyUpdate) => Promise<void>
  /** Send admin=true to bypass group search restrictions. Defaults to true since this dialog is used in admin contexts. */
  admin?: boolean
}

export function AccessPolicyDialog({
  open,
  onOpenChange,
  title,
  description,
  policy,
  isLoading,
  loadError = false,
  isSaving,
  inherit,
  onSave,
  admin = true,
}: AccessPolicyDialogProps) {
  const { t } = useTranslation()

  const [unrestricted, setUnrestricted] = useState(false)
  const [requiresEntries, setRequiresEntries] = useState<AccessEntry[]>([])
  const [blockEntries, setBlockEntries] = useState<AccessEntry[]>([])
  const [isInherited, setIsInherited] = useState(false)
  const [resolvedDirectory, setResolvedDirectory] = useState<ResolvedDirectoryMap>({})

  useEffect(() => {
    if (!open) return
    const normalized = normalizePolicy(policy)
    setUnrestricted(normalized.unrestricted)
    setRequiresEntries(toEntries(normalized.requires))
    setBlockEntries(toEntries(normalized.block))
  }, [open, policy])

  const inheritConfig = inherit?.enabled ? inherit : undefined
  const inheritPolicy = inheritConfig?.policy
  const inheritByDefault = inheritConfig?.inheritByDefault ?? false
  const inheritNormalized = useMemo(() => normalizePolicy(inheritPolicy), [inheritPolicy])
  const inheritRequiresEntries = useMemo(
    () => toEntries(inheritNormalized.requires),
    [inheritNormalized.requires]
  )
  const inheritBlockEntries = useMemo(
    () => toEntries(inheritNormalized.block),
    [inheritNormalized.block]
  )

  useEffect(() => {
    if (!open) return
    if (!inheritConfig) return
    setIsInherited(inheritByDefault)
  }, [open, inheritByDefault, inheritConfig])

  const isInheritMode = !!inheritConfig && isInherited
  const effectiveUnrestricted = isInheritMode ? inheritNormalized.unrestricted : unrestricted
  const effectiveRequiresEntries = isInheritMode ? inheritRequiresEntries : requiresEntries
  const effectiveBlockEntries = isInheritMode ? inheritBlockEntries : blockEntries

  const effectiveSelectedOids = useMemo(() => {
    return Array.from(
      new Set([...effectiveRequiresEntries, ...effectiveBlockEntries].map((e) => e.oid))
    ).filter(Boolean)
  }, [effectiveRequiresEntries, effectiveBlockEntries])

  useEffect(() => {
    if (!open) return
    if (effectiveSelectedOids.length === 0) return

    const runState = { cancelled: false }
    void (async () => {
      const unresolved = effectiveSelectedOids.filter((oid) => !resolvedDirectory[oid])
      if (unresolved.length === 0) return

      const results = await DirectorySearchService.resolveDirectoryObjects(unresolved)
      if (runState.cancelled) return

      if (results.length === 0) return
      setResolvedDirectory((prev) => {
        const next = { ...prev }
        for (const r of results) {
          next[r.oid] = r
        }
        return next
      })
    })()

    return () => {
      runState.cancelled = true
    }
  }, [open, effectiveSelectedOids, resolvedDirectory])

  const renderAccessEntry = (
    entry: AccessEntry,
    options: { variant: "requires" | "block"; onRemove: () => void; disabled: boolean }
  ) => {
    const resolved = resolvedDirectory[entry.oid]
    const displayName = entry.displayName || resolved?.displayName || entry.oid
    const secondaryText = resolved?.email || resolved?.jobTitle || resolved?.department
    const isUser = entry.type === "user"
    const Icon = isUser ? User : Users

    return (
      <div
        key={`${options.variant}-${entry.oid}`}
        className={cn(
          "flex items-center gap-3 rounded-md border px-3 py-2",
          options.variant === "block" ? "border-destructive/30 bg-destructive/5" : "bg-card"
        )}
      >
        <div
          className={cn(
            "flex size-8 shrink-0 items-center justify-center rounded-full",
            isUser
              ? "bg-primary/10 text-primary"
              : "border-foreground bg-secondary text-secondary-foreground border"
          )}
        >
          <Icon className="size-4" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-medium">{displayName}</span>
            <span
              className={cn(
                "shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium uppercase",
                options.variant === "block"
                  ? "border-foreground bg-destructive text-destructive-foreground/90 border"
                  : "bg-muted text-muted-foreground"
              )}
            >
              {entry.type}
            </span>
          </div>
          {secondaryText ? (
            <span className="text-muted-foreground block truncate text-xs">{secondaryText}</span>
          ) : null}
        </div>

        <Button
          aria-label={t("adminDashboard.access.remove", { defaultValue: "Remove" })}
          className="h-8 w-8 p-0"
          disabled={options.disabled}
          size="icon"
          variant="ghost"
          onClick={options.onRemove}
        >
          <Trash2 className="size-4" />
        </Button>
      </div>
    )
  }

  const addEntry = (target: "requires" | "block", result: DirectorySearchResult) => {
    const entry: AccessEntry = {
      oid: result.oid,
      type: result.type,
      displayName: result.displayName,
    }

    const exists = effectiveSelectedOids.includes(entry.oid)
    if (exists) return

    if (target === "requires") {
      setRequiresEntries((prev) => [...prev, entry])
    } else {
      setBlockEntries((prev) => [...prev, entry])
    }
  }

  const removeEntry = (target: "requires" | "block", oid: string) => {
    if (target === "requires") {
      setRequiresEntries((prev) => prev.filter((e) => e.oid !== oid))
    } else {
      setBlockEntries((prev) => prev.filter((e) => e.oid !== oid))
    }
  }

  const handleSave = async () => {
    if (loadError) return
    if (inheritConfig && isInherited) {
      const onSaveInherited = inheritConfig.onSaveInherited
      if (onSaveInherited) await onSaveInherited()
      onOpenChange(false)
      return
    }

    const payload = toUpdatePayload(unrestricted, requiresEntries, blockEntries)
    await onSave(payload)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner />
          </div>
        ) : null}

        {!isLoading && loadError ? (
          <DialogBody className="px-1">
            <Alert variant="destructive">
              <AlertTriangle className="size-4" />
              <AlertTitle>
                {t("adminDashboard.access.loadErrorTitle", {
                  defaultValue: "Could not load the access policy",
                })}
              </AlertTitle>
              <AlertDescription>
                {t("adminDashboard.access.loadErrorBody", {
                  defaultValue:
                    "Nothing has been changed. Close the dialog and try again. If it keeps failing, you may not have permission to manage access for this resource.",
                })}
              </AlertDescription>
            </Alert>
          </DialogBody>
        ) : null}

        {!isLoading && !loadError ? (
          <DialogBody className="space-y-6 px-1">
            {inheritConfig ? (
              <div className="bg-muted/30 flex items-start justify-between gap-4 rounded-lg border p-4">
                <div className="space-y-1">
                  <Label className="text-sm font-medium">
                    {inheritConfig.label ??
                      t("adminDashboard.access.inherit", {
                        defaultValue: "Inherit baseline access",
                      })}
                  </Label>
                  <p className="text-muted-foreground text-xs">
                    {inheritConfig.description ??
                      t("adminDashboard.access.inheritHelp", {
                        defaultValue:
                          "When enabled, this resource inherits the baseline access policy. Disable to customize.",
                      })}
                  </p>
                </div>
                <Switch
                  checked={isInherited}
                  disabled={isSaving}
                  onCheckedChange={setIsInherited}
                />
              </div>
            ) : null}

            <div className="bg-card flex items-start justify-between gap-4 rounded-lg border p-4">
              <div className="space-y-1">
                <Label className="text-sm font-medium">
                  {t("adminDashboard.access.unrestricted", { defaultValue: "Unrestricted" })}
                </Label>
                <p className="text-muted-foreground text-xs">
                  {t("adminDashboard.access.unrestrictedHelp", {
                    defaultValue:
                      "When enabled, all authenticated users can access (except those explicitly blocked).",
                  })}
                </p>
              </div>
              <Switch
                checked={effectiveUnrestricted}
                disabled={isSaving || isInheritMode}
                onCheckedChange={setUnrestricted}
              />
            </div>

            <div className="space-y-4">
              <div className="space-y-1">
                <Label className="text-sm font-medium">
                  {t("adminDashboard.access.requiresTitle", { defaultValue: "Allow list" })}
                </Label>
                <p className="text-muted-foreground text-xs">
                  {t("adminDashboard.access.requiresHelp", {
                    defaultValue:
                      "If you add users/groups here, only they can access (unless blocked). Leave empty to allow all (or rely on Unrestricted for index-level defaults).",
                  })}
                </p>
              </div>

              <DirectorySearch
                admin={admin}
                disabled={isSaving || effectiveUnrestricted || isInheritMode}
                selectedOids={effectiveSelectedOids}
                types={["users", "groups"]}
                placeholder={t("adminDashboard.access.searchPlaceholder", {
                  defaultValue: "Search users or groups...",
                })}
                onSelect={(result) => addEntry("requires", result)}
              />

              <div className="space-y-2">
                {effectiveRequiresEntries.length === 0 ? (
                  <span className="text-muted-foreground text-xs">
                    {t("adminDashboard.access.none", { defaultValue: "None" })}
                  </span>
                ) : (
                  effectiveRequiresEntries.map((entry) =>
                    renderAccessEntry(entry, {
                      variant: "requires",
                      disabled: isSaving || effectiveUnrestricted || isInheritMode,
                      onRemove: () => removeEntry("requires", entry.oid),
                    })
                  )
                )}
              </div>
            </div>

            <Separator />

            <div className="space-y-4">
              <div className="space-y-1">
                <Label className="text-sm font-medium">
                  {t("adminDashboard.access.blockTitle", { defaultValue: "Block list" })}
                </Label>
                <p className="text-muted-foreground text-xs">
                  {t("adminDashboard.access.blockHelp", {
                    defaultValue: "Blocked users/groups are denied even if allowed/unrestricted.",
                  })}
                </p>
              </div>

              <DirectorySearch
                admin={admin}
                disabled={isSaving || isInheritMode}
                selectedOids={effectiveSelectedOids}
                types={["users", "groups"]}
                placeholder={t("adminDashboard.access.searchPlaceholder", {
                  defaultValue: "Search users or groups...",
                })}
                onSelect={(result) => addEntry("block", result)}
              />

              <div className="space-y-2">
                {effectiveBlockEntries.length === 0 ? (
                  <span className="text-muted-foreground text-xs">
                    {t("adminDashboard.access.none", { defaultValue: "None" })}
                  </span>
                ) : (
                  effectiveBlockEntries.map((entry) =>
                    renderAccessEntry(entry, {
                      variant: "block",
                      disabled: isSaving || isInheritMode,
                      onRemove: () => removeEntry("block", entry.oid),
                    })
                  )
                )}
              </div>
            </div>
          </DialogBody>
        ) : null}

        <DialogFooter>
          <Button
            disabled={isSaving}
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t("adminDashboard.access.cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button disabled={isSaving || isLoading || loadError} type="button" onClick={handleSave}>
            {isSaving ? (
              <>
                <Spinner />
                {t("adminDashboard.access.saving", { defaultValue: "Saving..." })}
              </>
            ) : (
              t("adminDashboard.access.save", { defaultValue: "Save" })
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
